import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Inbox, Search, X } from 'lucide-react';
import { api } from '../../api/client';
import EmptyState from '../../components/EmptyState';
import PaginationFooter from '../../components/PaginationFooter';
import { SkeletonTable } from '../../components/Skeleton';
import { formatDateTime } from '../../lib/format';
import RawIpnLogDrawer from '../../components/RawIpnLogDrawer';

// Mirrors RawIpnLog.OUTCOMES in backend/src/models/RawIpnLog.js — keep in sync.
// Unknown values fall back to a gray pill below, so a new backend outcome won't break rendering.
const OUTCOME_LABEL = {
  processed: { label: 'Processed', color: 'bg-emerald-50 text-emerald-700' },
  duplicate: { label: 'Duplicate', color: 'bg-gray-100 text-gray-600' },
  parse_failed: { label: 'Parse failed', color: 'bg-rose-50 text-rose-700' },
  process_error: { label: 'Process error', color: 'bg-rose-50 text-rose-700' },
  missing_required_fields: { label: 'Missing fields', color: 'bg-amber-50 text-amber-700' },
  empty_body: { label: 'Empty body', color: 'bg-amber-50 text-amber-700' },
};

const OUTCOMES = Object.keys(OUTCOME_LABEL);

function OutcomePill({ outcome }) {
  const info = OUTCOME_LABEL[outcome] || { label: outcome, color: 'bg-gray-100 text-gray-500' };
  return <span className={`pill ${info.color}`}>{info.label}</span>;
}

export default function RawIpnLogsPage() {
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');
  // TODO: add a provider filter (backend already accepts ?provider=) once multi-provider IPN is live.
  const [outcome, setOutcome] = useState('');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState(null);
  const PAGE_SIZE = 25;

  useEffect(() => {
    const id = setTimeout(() => setQuery(searchInput), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [query, outcome]);

  const params = new URLSearchParams();
  if (query) params.set('q', query);
  if (outcome) params.set('outcome', outcome);
  params.set('page', String(page));
  params.set('limit', String(PAGE_SIZE));

  const logsQuery = useQuery({
    queryKey: ['admin-raw-ipn-logs', query, outcome, page, PAGE_SIZE],
    queryFn: () => api.get(`/api/admin/raw-ipn-logs?${params}`).then((r) => r.data),
    placeholderData: keepPreviousData,
  });

  const logs = logsQuery.data?.logs || [];
  const total = logsQuery.data?.total ?? 0;
  const totalPages = logsQuery.data?.totalPages ?? 1;
  const hasActiveFilter = Boolean(query || outcome);

  return (
    <>
      <div className="card">
        <div className="section-header flex-wrap gap-3">
          <div className="section-title">
            <span className="section-icon">
              <Inbox className="w-4 h-4" />
            </span>
            Raw IPN logs
            {logsQuery.data && (
              <span className="text-xs text-ink-400 font-normal ml-1">({total})</span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
              <input
                className="input pl-10 max-w-xs"
                placeholder="Search email, txn id, or IP…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <select
              className="input max-w-[170px]"
              value={outcome}
              onChange={(e) => setOutcome(e.target.value)}
            >
              <option value="">All outcomes</option>
              {OUTCOMES.map((k) => (
                <option key={k} value={k}>
                  {OUTCOME_LABEL[k].label}
                </option>
              ))}
            </select>
            {hasActiveFilter && (
              <button
                type="button"
                className="btn-ghost text-xs"
                onClick={() => {
                  setSearchInput('');
                  setQuery('');
                  setOutcome('');
                }}
              >
                <X className="w-3 h-3" /> Clear
              </button>
            )}
          </div>
        </div>

        {logsQuery.isLoading ? (
          <SkeletonTable rows={8} cols={5} />
        ) : !logs.length ? (
          <EmptyState
            icon={Inbox}
            title={hasActiveFilter ? 'No logs match.' : 'No IPN logs yet.'}
            body={
              hasActiveFilter
                ? 'Try clearing the filters.'
                : 'Every webhook POST from the cart will be recorded here, including ones that fail to parse.'
            }
          />
        ) : (
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-xs text-ink-500 uppercase tracking-wide">
                  <th className="text-left font-medium py-2 px-3">When</th>
                  <th className="text-left font-medium py-2 px-3">Outcome</th>
                  <th className="text-left font-medium py-2 px-3">Customer / Txn</th>
                  <th className="text-left font-medium py-2 px-3">Source IP</th>
                  <th className="text-left font-medium py-2 px-3">Linked sale</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logs.map((log) => (
                  <tr
                    key={log._id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedId(log._id)}
                  >
                    <td className="py-2 px-3 text-ink-700 whitespace-nowrap">
                      {formatDateTime(log.receivedAt)}
                    </td>
                    <td className="py-2 px-3">
                      <OutcomePill outcome={log.outcome} />
                    </td>
                    <td className="py-2 px-3">
                      <div className="text-ink-900">{log.customerEmail || '—'}</div>
                      {log.externalTxnId && (
                        <div className="text-xs text-ink-400 font-mono">{log.externalTxnId}</div>
                      )}
                    </td>
                    <td className="py-2 px-3 text-ink-700 font-mono text-xs">
                      {log.sourceIp || '—'}
                    </td>
                    <td className="py-2 px-3 text-ink-700">
                      {log.billingEventId ? (
                        <span className="pill bg-brand-50 text-brand-700 text-xs">linked</span>
                      ) : (
                        <span className="text-ink-300 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {logsQuery.data && totalPages > 1 && (
          <PaginationFooter
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            total={total}
            pageSize={PAGE_SIZE}
          />
        )}
      </div>

      <RawIpnLogDrawer
        id={selectedId}
        open={Boolean(selectedId)}
        onClose={() => setSelectedId(null)}
      />
    </>
  );
}
